<script setup lang="ts">
/**
 * 最終興行収入予測カードの本体（DEV-14891 / DEV-11292）
 * 後端 GET /dp/boxOfficePrediction/latest の応答をそのまま受け取り、段階（stage）ごとに表示を切り替える。
 * 現時点では全段階とも「規模の目安（档位）」のみ表示し、予測値・区間は応答に含まれていても表示しない（2026-09-17 決定）。
 */
import { computed } from 'vue';
import dayjs from 'dayjs';

const props = defineProps<{
  /** /dp/boxOfficePrediction/latest の応答。取得失敗時は null */
  prediction: any | null;
  loading: boolean;
}>();

/** 予測を出した段階（none 以外） */
const hasTier = computed(() => ['early', 'd1', 'post'].includes(props.prediction?.stage));

/** 分界線（億円・昇順）から各档位の文言を作る：[1, 10] → 1億未満 / 1〜10億 / 10億以上 */
const tierLabels = computed<string[]>(() => {
  const bounds: number[] = props.prediction?.tierBounds ?? [];
  if (!bounds.length) return [];
  const fmt = (n: number) => String(n);
  return [
    `${fmt(bounds[0])}億未満`,
    ...bounds.slice(1).map((b, i) => `${fmt(bounds[i])}〜${fmt(b)}億`),
    `${fmt(bounds[bounds.length - 1])}億以上`,
  ];
});

/** 段階ごとの説明文 */
const STAGE_NOTE: Record<string, string> = {
  early: '公開前の早期予測のため、規模感の目安としてご覧ください。',
  d1: '公開直前の上映スケジュールを反映した予測です。',
  post: '公開後の興行実績を反映した予測です。',
};

/** 予測なし（stage = none）の理由ごとの文言 */
const REASON_TEXT: Record<string, string> = {
  TOO_EARLY: '公開28日前から予測を表示します。',
  NOT_TARGET: 'この作品は予測の対象外です（イベント上映・ライブ配信など）。',
  NO_SOCIAL: 'SNS データが不足しているため、予測できません。',
  FEW_CINEMAS: '上映館数が少ないため、予測の対象外です。',
  NO_BOX_OFFICE: '興行データがまだないため、予測できません。',
  NO_DATA: 'データが不足しているため、予測できません。',
  OUT_OF_RANGE: '予測の対象期間外です（公開28日前〜公開60日後）。',
  NOT_RUN: '予測データを取得できませんでした。',
};

/** 右上の「公開まで N日 / 公開当日 / 公開から N日」 */
const timingText = computed(() => {
  const d = props.prediction?.daysToRelease;
  if (d == null || props.prediction?.reason === 'ENDED') return '';
  if (d > 0) return `公開まで ${d}日`;
  if (d === 0) return '公開当日';
  return `公開から ${-d}日`;
});

const emptyText = computed(() => {
  const p = props.prediction;
  if (!p) return REASON_TEXT.NOT_RUN;
  const text = REASON_TEXT[p.reason] ?? REASON_TEXT.NOT_RUN;
  // 公開29〜61日前：あと何日で予測が始まるかを添える
  if (p.reason === 'TOO_EARLY' && p.daysToRelease > 28) return `${text}（あと ${p.daysToRelease - 28}日）`;
  return text;
});

/** 予測日（M月D日）。当日分の定期処理が失敗した場合は古い日付になる */
const asOfText = computed(() => {
  const d = props.prediction?.predictDate;
  return d ? `${dayjs(d).format('M月D日')}時点の予測` : '';
});
</script>

<template>
  <div class="fp-body">
    <div v-if="loading" class="fp-empty">
      <i class="fas fa-spinner fa-spin"></i>
      <span>読み込み中…</span>
    </div>
    <template v-else-if="hasTier">
      <div class="fp-meta">
        <span class="fp-caption">最終興行収入の規模（目安）</span>
        <span v-if="timingText" class="fp-timing">{{ timingText }}</span>
      </div>
      <div class="fp-tiers" :style="{ gridTemplateColumns: `repeat(${tierLabels.length}, minmax(0, 1fr))` }">
        <div
          v-for="(label, i) in tierLabels"
          :key="label"
          class="fp-tier"
          :class="{ 'is-active': i === prediction.tierIndex }"
        >{{ label }}</div>
      </div>
      <p class="fp-note">
        <template v-if="prediction.reason === 'ENDED'">公開から60日が経過したため予測を終了しました（最終予測）。</template>
        <template v-else>{{ STAGE_NOTE[prediction.stage] }}</template>
      </p>
      <p v-if="asOfText" class="fp-asof" :class="{ 'is-stale': prediction.stale }">
        <i v-if="prediction.stale" class="fas fa-exclamation-triangle"></i>
        {{ asOfText }}<template v-if="prediction.stale">（最新の予測を取得できませんでした）</template>
      </p>
    </template>
    <div v-else class="fp-empty">
      <i class="fas fa-hourglass-half"></i>
      <span>{{ emptyText }}</span>
    </div>
  </div>
</template>

<style scoped lang="less">
.fp-body {
  padding-top: 4px;
}

.fp-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.fp-timing {
  font-weight: 600;
  white-space: nowrap;
}

.fp-tiers {
  display: grid;
  gap: 6px;
}

.fp-tier {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 6px 4px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  color: var(--text-secondary);
  font-size: 0.85rem;
  text-align: center;
  word-break: keep-all;

  &.is-active {
    border-color: var(--brand-color);
    background: var(--brand-color);
    color: #fff;
    font-size: 1rem;
    font-weight: 700;
  }

  @media (max-width: 576px) {
    min-height: 40px;
    font-size: 0.75rem;

    &.is-active {
      font-size: 0.85rem;
    }
  }
}

.fp-note {
  margin: 12px 0 0;
  color: var(--text-secondary);
  font-size: 0.85rem;
}

.fp-asof {
  margin: 4px 0 0;
  color: var(--text-secondary);
  font-size: 0.8rem;

  &.is-stale {
    color: #e6a23c;
  }
}

.fp-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 120px;
  color: var(--text-secondary);
  font-size: 1rem;
  font-weight: 600;
  text-align: center;

  i {
    color: var(--brand-color);
  }
}
</style>
